import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  BellRing,
  Loader2,
  MessageSquare,
  AtSign,
  ListChecks,
  Mic,
  Moon,
  Smartphone,
  Send,
} from "lucide-react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/constants";
import {
  subscribeToPush,
  unsubscribeFromPush,
  currentSubscription,
  pushSupported,
  isIOS,
  isStandalone,
} from "@/lib/push";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const KIND_ROWS = [
  { key: "messages", label: "New channel messages", icon: MessageSquare },
  { key: "mentions", label: "@mentions & thread replies", icon: AtSign },
  { key: "tasks", label: "Task picked up or completed", icon: ListChecks },
  { key: "meetings", label: "New meeting recordings & notes", icon: Mic },
];

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const hourLabel = (h) => `${String(h).padStart(2, "0")}:00`;

export default function NotificationSettings({ open, onOpenChange, channels = [] }) {
  const [prefs, setPrefs] = useState(null);
  const [sms, setSms] = useState({ configured: false, missing: [] });
  const [saving, setSaving] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [busyPush, setBusyPush] = useState(false);

  useEffect(() => {
    if (!open) return;
    api
      .get("/notifications/prefs")
      .then((r) => {
        setPrefs(r.data.prefs);
        setSms(r.data.sms);
      })
      .catch((err) => console.error("Failed to load prefs:", err));
    currentSubscription()
      .then((s) => setPushOn(Boolean(s)))
      .catch(() => setPushOn(false));
  }, [open]);

  const save = async (patch) => {
    setPrefs((p) => ({ ...p, ...patch }));
    setSaving(true);
    try {
      const r = await api.put("/notifications/prefs", patch);
      setPrefs(r.data.prefs);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save settings");
      const r = await api.get("/notifications/prefs").catch(() => null);
      if (r) setPrefs(r.data.prefs);
    } finally {
      setSaving(false);
    }
  };

  const togglePush = async () => {
    setBusyPush(true);
    try {
      if (pushOn) {
        await unsubscribeFromPush();
        setPushOn(false);
        await save({ push_enabled: false });
        toast.success("Push notifications turned off on this device");
      } else {
        await subscribeToPush();
        setPushOn(true);
        await save({ push_enabled: true });
        toast.success("Push notifications enabled on this device");
      }
    } catch (e) {
      toast.error(e.message || "Could not enable push notifications");
    } finally {
      setBusyPush(false);
    }
  };

  const sendTest = async () => {
    try {
      await api.post("/push/test");
      toast.success("Test notification sent — check your device");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Test failed");
    }
  };

  const qh = prefs?.quiet_hours || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#121318] border-[#2E3242] text-gray-200 max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="notification-settings-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-gray-100">
            <BellRing className="w-5 h-5 text-red-400" /> Notification settings
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            Choose what pings you, on which device, and when to stay quiet.
          </DialogDescription>
        </DialogHeader>

        {!prefs ? (
          <div className="py-10 flex justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-red-400" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Push */}
            <section className="rounded-xl border border-[#2E3242] bg-[#1E2029] p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-100">Pop-up notifications on this device</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {pushSupported()
                      ? "Alerts appear even when DevHub is closed."
                      : "This browser does not support push notifications."}
                  </p>
                </div>
                <Button
                  data-testid="toggle-push-button"
                  onClick={togglePush}
                  disabled={busyPush || !pushSupported()}
                  className={
                    pushOn
                      ? "bg-[#262936] hover:bg-[#2E3242] text-gray-200 border border-[#2E3242] h-9 shrink-0"
                      : "ruby-gradient text-white border border-red-900/40 h-9 shrink-0"
                  }
                >
                  {busyPush ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : pushOn ? (
                    "Turn off"
                  ) : (
                    "Enable"
                  )}
                </Button>
              </div>
              {isIOS() && !isStandalone() && (
                <p className="mt-3 text-xs text-amber-300 bg-amber-950/30 border border-amber-900/40 rounded-lg p-2.5">
                  iPhone/iPad: tap <strong>Share → Add to Home Screen</strong>, then open DevHub from the
                  new icon to allow pop-up notifications.
                </p>
              )}
              {pushOn && (
                <button
                  data-testid="send-test-push-button"
                  onClick={sendTest}
                  className="mt-3 flex items-center gap-1.5 text-xs text-red-300 hover:text-red-200"
                >
                  <Send className="w-3.5 h-3.5" /> Send me a test notification
                </button>
              )}
            </section>

            {/* Kinds */}
            <section>
              <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Notify me about</p>
              <div className="space-y-1">
                {KIND_ROWS.map(({ key, label, icon: Icon }) => (
                  <label
                    key={key}
                    className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-[#1E2029] transition-colors cursor-pointer"
                  >
                    <Icon className="w-4 h-4 text-gray-500 shrink-0" />
                    <span className="text-sm text-gray-300 flex-1">{label}</span>
                    <Switch
                      data-testid={`pref-kind-${key}`}
                      checked={!!prefs.kinds?.[key]}
                      onCheckedChange={(v) => save({ kinds: { ...prefs.kinds, [key]: v } })}
                      className="data-[state=checked]:bg-red-600"
                    />
                  </label>
                ))}
              </div>
            </section>

            {/* Quiet hours */}
            <section className="rounded-xl border border-[#2E3242] bg-[#1E2029] p-4">
              <div className="flex items-center gap-3">
                <Moon className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-200 flex-1">Quiet hours</span>
                <Switch
                  data-testid="pref-quiet-hours"
                  checked={!!qh.enabled}
                  onCheckedChange={(v) =>
                    save({
                      quiet_hours: {
                        ...qh,
                        enabled: v,
                        tz_offset: -new Date().getTimezoneOffset(),
                      },
                    })
                  }
                  className="data-[state=checked]:bg-red-600"
                />
              </div>
              {qh.enabled && (
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <label className="text-xs text-gray-500">From</label>
                  <select
                    data-testid="quiet-start-select"
                    value={qh.start ?? 22}
                    onChange={(e) =>
                      save({ quiet_hours: { ...qh, start: Number(e.target.value), tz_offset: -new Date().getTimezoneOffset() } })
                    }
                    className="bg-[#16181F] border border-[#2E3242] rounded-lg text-sm px-2 py-1.5 text-gray-200"
                  >
                    {HOURS.map((h) => (
                      <option key={h} value={h}>{hourLabel(h)}</option>
                    ))}
                  </select>
                  <label className="text-xs text-gray-500">to</label>
                  <select
                    data-testid="quiet-end-select"
                    value={qh.end ?? 8}
                    onChange={(e) =>
                      save({ quiet_hours: { ...qh, end: Number(e.target.value), tz_offset: -new Date().getTimezoneOffset() } })
                    }
                    className="bg-[#16181F] border border-[#2E3242] rounded-lg text-sm px-2 py-1.5 text-gray-200"
                  >
                    {HOURS.map((h) => (
                      <option key={h} value={h}>{hourLabel(h)}</option>
                    ))}
                  </select>
                  <span className="text-xs text-gray-600">your local time</span>
                </div>
              )}
            </section>

            {/* Muted channels */}
            <section>
              <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Channels — tap to mute</p>
              <div className="flex flex-wrap gap-2">
                {channels.map((c) => {
                  const muted = (prefs.muted_channels || []).includes(c.id);
                  return (
                    <button
                      key={c.id}
                      data-testid={`mute-channel-${c.id}`}
                      onClick={() =>
                        save({
                          muted_channels: muted
                            ? prefs.muted_channels.filter((x) => x !== c.id)
                            : [...(prefs.muted_channels || []), c.id],
                        })
                      }
                      className={`px-2.5 py-1.5 rounded-full text-xs border transition-colors ${
                        muted
                          ? "bg-red-950/50 border-red-900/50 text-red-200 line-through"
                          : "bg-[#1E2029] border-[#2E3242] text-gray-400 hover:border-red-900/40"
                      }`}
                    >
                      #{c.name}
                    </button>
                  );
                })}
              </div>
            </section>

            {/* SMS */}
            <section className="rounded-xl border border-[#2E3242] bg-[#1E2029] p-4">
              <div className="flex items-center gap-3">
                <Smartphone className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-200 flex-1">
                  Text me high-priority alerts
                  <span className="block text-xs text-gray-500 mt-0.5">
                    @mentions and high-priority task updates only
                  </span>
                </span>
                <Switch
                  data-testid="pref-sms"
                  checked={!!prefs.sms_enabled}
                  disabled={!sms.configured}
                  onCheckedChange={(v) => save({ sms_enabled: v })}
                  className="data-[state=checked]:bg-red-600"
                />
              </div>
              {!sms.configured && (
                <p className="mt-3 text-xs text-amber-300">
                  SMS is off until a Twilio Account SID (AC…) and a Twilio phone number are added on the
                  server.
                </p>
              )}
              <Input
                data-testid="sms-phone-input"
                value={prefs.phone || ""}
                disabled={!sms.configured}
                onChange={(e) => setPrefs((p) => ({ ...p, phone: e.target.value }))}
                onBlur={() => save({ phone: prefs.phone || "" })}
                placeholder="+14155552671"
                className="mt-3 bg-[#16181F] border-[#2E3242] focus-visible:ring-red-600 h-10 text-sm disabled:opacity-50"
              />
            </section>

            <p className="text-[11px] text-gray-600 text-right h-4">{saving ? "Saving…" : ""}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
